import React from 'react';
import { Campaign } from '../types/campaign';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BarChartIcon from '@mui/icons-material/BarChart';
import CampaignAnalyticsModal from './CampaignAnalyticsModal';

interface CampaignListProps {
  campaigns: Campaign[];
  onEdit: (campaign: Campaign) => void;
  onDelete: (id: number) => Promise<void>;
  onPause: (id: number) => Promise<void>;
  onResume: (id: number) => Promise<void>;
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return {
        color: '#059669',
        backgroundColor: '#064E3B',
      };
    case 'paused':
      return {
        color: '#D97706',
        backgroundColor: '#78350F',
      };
    default:
      return {
        color: '#6B7280',
        backgroundColor: '#374151',
      };
  }
};

const CampaignList: React.FC<CampaignListProps> = ({ campaigns, onEdit, onDelete, onPause, onResume }) => {
  const [analyticsOpen, setAnalyticsOpen] = React.useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = React.useState<number | null>(null);

  const handleOpenAnalytics = (campaignId: number) => {
    setSelectedCampaignId(campaignId);
    setAnalyticsOpen(true);
  };
  const handleCloseAnalytics = () => {
    setAnalyticsOpen(false);
    setSelectedCampaignId(null);
  };
  
  // Helper to check status regardless of case
  const isActive = (status: string): boolean => {
    return status.toLowerCase() === 'active';
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ bgcolor: '#1F2937', borderRadius: 2, border: '1px solid #374151' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                color: '#9CA3AF', 
                borderBottom: '1px solid #374151',
                fontSize: '0.875rem',
                fontWeight: 600 
              }}>
                Campaign Name
              </TableCell>
              <TableCell sx={{ 
                color: '#9CA3AF', 
                borderBottom: '1px solid #374151',
                fontSize: '0.875rem',
                fontWeight: 600 
              }}>
                Status
              </TableCell>
              <TableCell sx={{ 
                color: '#9CA3AF', 
                borderBottom: '1px solid #374151',
                fontSize: '0.875rem',
                fontWeight: 600 
              }}>
                Budget
              </TableCell>
              <TableCell sx={{ 
                color: '#9CA3AF', 
                borderBottom: '1px solid #374151',
                fontSize: '0.875rem',
                fontWeight: 600,
                width: '180px'
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => {
              const statusStyle = getStatusColor(campaign.status);
              return (
                <TableRow 
                  key={campaign.id}
                  sx={{ '&:hover': { bgcolor: '#374151' } }}
                >
                  <TableCell 
                    sx={{ 
                      color: '#E5E7EB',
                      borderBottom: '1px solid #374151'
                    }}
                  >
                    {campaign.title}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      borderBottom: '1px solid #374151'
                    }}
                  >
                    <Chip
                      label={campaign.status}
                      sx={{
                        color: statusStyle.color,
                        bgcolor: statusStyle.backgroundColor,
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        height: '24px'
                      }}
                    />
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: '#E5E7EB',
                      borderBottom: '1px solid #374151'
                    }}
                  >
                    ${campaign.budget.toFixed(2)}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      borderBottom: '1px solid #374151'
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(campaign)}
                          sx={{ 
                            color: '#60A5FA',
                            '&:hover': { bgcolor: '#1E40AF' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isActive(campaign.status) ? (
                        <Tooltip title="Pause">
                          <IconButton
                            size="small"
                            onClick={() => onPause(campaign.id)}
                            sx={{ 
                              color: '#FBBF24',
                              '&:hover': { bgcolor: '#92400E' }
                            }}
                          >
                            <PauseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Resume">
                          <IconButton
                            size="small"
                            onClick={() => onResume(campaign.id)}
                            sx={{ 
                              color: '#34D399',
                              '&:hover': { bgcolor: '#065F46' }
                            }}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(campaign.id)}
                          sx={{ 
                            color: '#EF4444',
                            '&:hover': { bgcolor: '#991B1B' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Analytics">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenAnalytics(campaign.id)}
                          sx={{ color: '#3B82F6', '&:hover': { bgcolor: '#1E40AF' } }}
                        >
                          <BarChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CampaignAnalyticsModal
        open={analyticsOpen}
        onClose={handleCloseAnalytics}
        campaignId={selectedCampaignId}
      />
    </>
  );
};

export default CampaignList; 